import { ReactNode } from 'react';
import { InputAdornment, Box, TextField, useTheme } from '@mui/material';
import { RHFAutocomplete } from 'src/components/minimals/form';
import { LightTooltip } from 'src/components/minimals/tooltip/LightTooltip';
import { isTagEditable } from 'src/features/widgets/utils';
import { LatestControlsForInhandTypes } from 'src/features/widgets/hooks/types';
import { textField } from './style';
import { filterOptions } from 'src/utilities';
import { STATE } from 'src/constants';
import { LocalStorageService } from 'src/features/authentication/utils';
import { useAuthentication } from 'src/features/authentication/context';
import { AccessRoles } from 'src/features/user-account-details/types';
import { useResponsive } from 'src/hooks/useResponsive';
import { ControllerAdditionalInfo } from 'src/types/controllers/controller';
import { LabelingTypeEnum } from 'src/types';
import { InfoIcon } from 'src/assets/icons/InfoIcon';

type DropdownModalItemProps = {
  getInputOptions: (item: any) => string[] | { label: string; value: string }[];
  setValue: (key: string, value: string) => void;
  titleTooltip: (item: any) => ReactNode;
  item: ControllerAdditionalInfo;
  title: string;
  selectedMode?: { id?: string; displayName?: string; value?: string };
  latestControlsForInhand: LatestControlsForInhandTypes[];
  isErrorOnUpdateControls: boolean;
  enableApplyButtonHandler: () => void;
  isDataLoading: boolean;
  isAppliedChanges: boolean;
  setIsAppliedChangedHandler: (value: boolean) => void;
  isCloseBtn: boolean;
  isControllable: boolean;
};

export const DropdownModalItem = ({
  getInputOptions,
  titleTooltip,
  item,
  title,
  selectedMode,
  latestControlsForInhand,
  isErrorOnUpdateControls,
  enableApplyButtonHandler,
  isDataLoading,
  setValue,
  isAppliedChanges,
  setIsAppliedChangedHandler,
  isCloseBtn,
  isControllable,
}: DropdownModalItemProps) => {
  const theme = useTheme();
  const isStateDataType = item?.dataType === STATE;
  const rawUnit = item.rawUnit ? `(${item.rawUnit})` : '';
  const tagId = item?.id;
  const latestControlItem = latestControlsForInhand?.find((item) => item.tagId === tagId);

  const {
    customerId: { value: customerId },
    siteId: { value: siteId },
    getCurrentRole,
  } = useAuthentication();

  const isSiteEmployee = getCurrentRole(customerId, siteId) === AccessRoles.SITE_EMPLOYEE;

  const isErrorShown = window.localStorage.getItem(tagId) === 'error-seen';

  if (latestControlItem && latestControlItem?.success === false && !isErrorShown) {
    LocalStorageService.set(tagId, 'error');
  }

  const isSuccessFalse = !latestControlItem?.success && !isDataLoading;

  const isControlError = isSuccessFalse || isErrorOnUpdateControls;

  const errorMessage =
    (latestControlItem && latestControlItem?.success === false && isAppliedChanges) || !isErrorShown
      ? `Failed to change to ${latestControlItem?.newValue}`
      : '';
  const successMessage =
    isAppliedChanges && !isDataLoading && latestControlItem && latestControlItem?.success
      ? 'Successfully updated'
      : '';

  const isMobile = useResponsive('down', 'md');
  const disableFiled =
    isCloseBtn || isDataLoading || !isTagEditable({ item: item, selectedMode: selectedMode });

  const inputOptions = getInputOptions(item);

  const labellingType = item?.labellingType;
  const fallbackValue = item?.fallbackValue ?? '';
  const userDefinedValues = item?.advancedTagMapping?.userDefinedValues;

  // The option strings stay canonical ("value - name") so the submit parser keeps
  // working; we only re-render each option based on the tag's labellingType.
  const getOptionDisplay = (option: string) => {
    if (!option) return '';
    // Options are either "value - name" (predefined) or the bare device value when the
    // live value has no mapping, so read the leading number instead of splitting on "-".
    const valueToken = option.match(/^\s*-?\d+(?:\.\d+)?/)?.[0].trim() ?? option.trim();
    const userDefinedValue = userDefinedValues?.find(
      (udv) => Number(udv.value) === Number(valueToken)
    );

    switch (labellingType) {
      case LabelingTypeEnum.VALUE:
        return userDefinedValue ? `${userDefinedValue.value}` : valueToken;
      case LabelingTypeEnum.LABEL:
        return userDefinedValue?.name || fallbackValue;
      case LabelingTypeEnum.VALUE_LABEL: {
        // Unmapped device values still have to show the value, with the fallback as label.
        const value = userDefinedValue?.value ?? valueToken;
        const name = userDefinedValue?.name || fallbackValue;
        return name ? `${value} - ${name}` : `${value}`;
      }
      default:
        return option;
    }
  };

  const onChangeHandler = (value: string) => {
    setIsAppliedChangedHandler(false);
    const key = `${item.dataType}-${item.id}`;

    setValue(key, value);
    enableApplyButtonHandler();
  };

  return (
    <RHFAutocomplete
      disabled={disableFiled || isSiteEmployee || !isControllable}
      onChange={(_, newValue) => onChangeHandler(newValue as string)}
      name={`${item.dataType}-${item.id}`}
      options={['', ...(inputOptions as string[])]}
      getOptionLabel={(option) => getOptionDisplay(option as string)}
      filterOptions={(options: string[], state: any) =>
        filterOptions(options, state, ['', ...(inputOptions as string[])])
      }
      renderInput={(params) => (
        <TextField
          sx={textField(isControlError, theme, !!successMessage)}
          helperText={
            isErrorOnUpdateControls || (isSuccessFalse && latestControlItem)
              ? errorMessage
              : successMessage
          }
          {...params}
          fullWidth
          inputProps={{
            ...params.inputProps,
            'data-sm': `${title}-input`,
            readOnly: isMobile,
          }}
          label={`${item.displayName} ${!isStateDataType ? rawUnit : ''}`}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <LightTooltip
                  arrow
                  title={titleTooltip(item)}
                  placement="right-start"
                  enterTouchDelay={0}
                >
                  <Box>
                    <InfoIcon />
                  </Box>
                </LightTooltip>
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};
